import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

import { IDriveContent, IDriveContentAncestors } from 'src/@types/drive';
import { DefaultContextType } from 'src/@types/react-query';
import { AuthUserType } from 'src/auth/types';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useDriveContentQuery } from 'src/hooks/useDriveContentQuery';
import {
  DriveAddFilePayload,
  DriveCreateFolderPayload,
  DriveRenameFolderPayload,
  DriveUpdateFavoritePayload,
} from 'src/requests/drive';
import { useDownloadDrive } from './DownloadDriveContent';
import { useContentAncestorsQuery } from './shared/useContentAncestorsQuery';
import useAddFileMutation from './useAddFileMutation';
import { useCreateFolderMutation } from './useCreateFolderMutation';
import { useDeleteContent } from './useDeleteContent';
import { useFavoriteContentMutation } from './useFavoriteContentMutation';
import { useRenameContent } from './useRenameContent';

const STATE_KEY = 'driveContents';

type State = {
  [STATE_KEY]: IDriveContent[];
};

type DriveContextType = State &
  DefaultContextType & {
    createFolder(_payload: DriveCreateFolderPayload): Promise<IDriveContent>;
    renameContent(_payload: DriveRenameFolderPayload): Promise<IDriveContent>;
    addFile(_payload: DriveAddFilePayload): Promise<IDriveContent>;
    deleteContent(_id: string): Promise<IDriveContent>;
    downloadContent(file: IDriveContent): Promise<void>;
    setFavorite(_payload: DriveUpdateFavoritePayload): Promise<IDriveContent>;
    currentUser?: AuthUserType;
    ancestors?: IDriveContentAncestors;
  };

const initialState: DriveContextType = {
  [STATE_KEY]: [],

  isLoading: true,
  isFetching: true,
  createFolder(): any {},
  renameContent(): any {},
  addFile(): any {},
  deleteContent(): any {},
  downloadContent(): any {},
  setFavorite(): any {},
};

const DriveContext = createContext(initialState);

export function useDriveContext() {
  const context = useContext(DriveContext);

  if (!context)
    throw new Error('useDriveContext must be use inside DriveContextProvider');

  return context;
}

export function DriveContextProvider({ children }: { children: ReactNode }) {
  const { folderId = '' } = useParams();

  const { user } = useAuthContext();
  const { ancestors, isAncestorsLoading } = useContentAncestorsQuery(folderId);

  const { driveContents, isDriveContentLoading } =
    useDriveContentQuery(folderId);
  const { addFile } = useAddFileMutation(folderId);
  const { downloadContent } = useDownloadDrive();
  const { deleteContent } = useDeleteContent();
  const { setFavorite } = useFavoriteContentMutation();
  const { renameContent } = useRenameContent();
  const { createFolder } = useCreateFolderMutation();

  const memoizedValue = useMemo(
    () => ({
      driveContents: driveContents || [],
      isLoading: isDriveContentLoading,
      isFetching: isDriveContentLoading,

      createFolder,
      renameContent,
      addFile,
      deleteContent,
      downloadContent,
      setFavorite,
      isAncestorsLoading,
      ancestors: folderId ? ancestors : undefined,
      currentUser: user,
    }),
    [
      driveContents,
      isDriveContentLoading,
      createFolder,
      renameContent,
      addFile,
      deleteContent,
      downloadContent,
      setFavorite,
      ancestors,
      user,
      folderId,
      isAncestorsLoading,
    ],
  );

  return (
    <DriveContext.Provider value={memoizedValue}>
      {children}
    </DriveContext.Provider>
  );
}
